import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, ArrowRight } from "lucide-react";
import AphroditesWaitlist from "@/components/AphroditesWaitlist";

const AphroditesRoots = () => {
    const [products, setProducts] = useState([]);

    useEffect(() => {
        api.get("/products").then((r) => setProducts(r.data.products)).catch(() => {});
    }, []);

    return (
        <div data-testid="aphrodites-page">
            {/* HERO */}
            <section className="relative py-32 md:py-44 hl-grain overflow-hidden bg-hl-tinted/40">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center relative z-10">
                    <div className="inline-flex items-center gap-2 mb-6 text-hl-secondary">
                        <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                        <span className="text-[11px] uppercase tracking-[0.3em]">grown in cyprus · one ingredient at a time</span>
                        <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                    <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-hl-brown leading-[0.98]">
                        Aphrodite's <span className="italic">Roots</span>
                    </h1>
                    <p className="font-script text-3xl md:text-4xl text-hl-secondary mt-6">
                        the bones of every recipe in this archive
                    </p>
                    <p className="mt-10 max-w-2xl mx-auto text-hl-muted text-lg leading-relaxed">
                        We're starting with two — the ingredients we reach for on almost every
                        page of iCookGreek. Wild oregano hand-picked from the Cypriot hills, and
                        the slow-cooked carob syrup our grandparents called <em>black gold</em>.
                        Both grown, dried, and bottled in Cyprus. Both available in a limited first run.
                    </p>
                    <p className="mt-6 text-[11px] uppercase tracking-[0.3em] text-hl-gold">
                        Launching 2026 · Limited first batch · Preorder open
                    </p>
                </div>
            </section>

            {/* PRODUCTS */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
                <div className="grid gap-16 md:gap-20">
                    {products.map((p, i) => (
                        <div
                            key={p.id}
                            data-testid={`product-${p.id}`}
                            className={`grid lg:grid-cols-12 gap-10 md:gap-16 items-center hl-fade-up ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
                        >
                            <div className="lg:col-span-7 [direction:ltr]">
                                <div className="aspect-[5/4] overflow-hidden rounded-sm hl-shadow-deep">
                                    <img
                                        src={p.image}
                                        alt={p.name}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105"
                                    />
                                </div>
                            </div>
                            <div className="lg:col-span-5 [direction:ltr]">
                                <p className="text-[11px] uppercase tracking-[0.3em] text-hl-muted mb-3">
                                    {p.origin} · No. {(i + 1).toString().padStart(2, "0")}
                                </p>
                                <h2 className="font-serif text-4xl md:text-5xl text-hl-brown leading-[1.05]">
                                    {p.name}
                                </h2>
                                <p className="font-script text-2xl text-hl-secondary mt-3">
                                    {p.tagline}
                                </p>
                                <p className="mt-6 text-hl-text leading-relaxed text-lg">
                                    {p.description}
                                </p>
                                <a
                                    href="https://aphroditeroots.com/?utm_source=icookgreek&utm_medium=ebook&utm_campaign=ebook_showcase&utm_content=roots_page_button"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`product-preorder-${p.id}`}
                                    className="mt-8 inline-flex items-center gap-3 px-7 py-4 bg-hl-secondary text-hl-bg hover:bg-hl-secondaryDeep hl-btn-lift text-[11px] uppercase tracking-[0.3em]"
                                >
                                    Preorder now — be the first to taste it
                                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                                </a>
                                <p className="mt-3 text-[11px] uppercase tracking-[0.25em] text-hl-muted flex items-center gap-2">
                                    <Sparkles className="w-3.5 h-3.5 text-hl-gold" strokeWidth={1.5} />
                                    Launching 2026 · Limited first batch
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section id="waitlist" className="bg-hl-brown text-hl-bg py-24 hl-grain scroll-mt-20">
                <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
                    <p className="font-script text-3xl text-[#E8D9B5]">be the first to taste it —</p>
                    <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">
                        The first batch is open for <span className="italic">preorder</span>.
                    </h2>
                    <p className="text-hl-bg/75 mt-6 max-w-xl mx-auto">
                        Wild oregano from the Troodos hills and slow-cooked Cypriot carob syrup —
                        grown, dried, and bottled in Cyprus. Limited first run, shipped when the
                        harvest is ready.
                    </p>

                    <a
                        href="https://aphroditeroots.com/?utm_source=icookgreek&utm_medium=ebook&utm_campaign=ebook_showcase&utm_content=roots_page_button"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="waitlist-shop-cta"
                        className="mt-10 inline-flex items-center gap-3 px-8 py-4 bg-[#E8D9B5] text-hl-brown hover:bg-[#f0e4c8] hl-btn-lift text-[11px] uppercase tracking-[0.3em]"
                    >
                        Preorder on our shop
                        <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </a>

                    <p className="mt-12 text-sm text-hl-bg/60 max-w-md mx-auto">
                        Prefer a nudge instead? Leave your email and we'll write once when the
                        first batch ships. No spam.
                    </p>
                    <div className="mt-6">
                        <AphroditesWaitlist />
                    </div>

                    <p className="mt-8 text-xs text-hl-bg/50">
                        Or follow{" "}
                        <Link to="/" className="hl-link text-[#E8D9B5]">@icookgreek</Link>
                        {" "}on TikTok and Instagram.
                    </p>
                </div>
            </section>
        </div>
    );
};

export default AphroditesRoots;
